import { AdminTable } from './shared/AdminDataGrid';
/**
 * User Usage Show Component
 * Displays per-feature usage stats with bonus management for a specific user
 */

import { useState, useEffect } from 'react';
import { useSharedAllowance } from '../hooks/useSharedAllowance';
import { useAdminQuery } from '../hooks/useAdminQuery';
import { useAdminText } from './shared/useAdminText';
import { useParams, Link as RouterLink } from 'react-router-dom';
import { useNotify, TopToolbar } from 'react-admin';
import {
  Card,
  CardContent,
  Typography,
  Button,
  Box,
  Chip,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Tooltip,
  Breadcrumbs,
  Link,
  Alert,
  Divider,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PeopleIcon from '@mui/icons-material/People';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';

import { adminApi } from '../providers/dataProvider';
import { logger } from '../utils/logger';
import { PLAN_TIER_FULL_LABELS } from '../constants';

type FeatureUsage = {
  shared?: { pairId: string; version: number };
  feature: string;
  displayName: string;
  used: number;
  planLimit: number;
  bonus: number;
  purchasedSlots?: number;
  effectiveLimit: number;
  remaining: number;
  unlimited: boolean;
  resetPeriod: string;
};

/**
 * Edit Bonus Dialog
 */
function EditBonusDialog({
  open,
  onClose,
  userId,
  feature,
  currentBonus,
  onSaved,
  removing,
}: {
  open: boolean;
  onClose: () => void;
  userId: string;
  feature: FeatureUsage | null;
  currentBonus: number;
  onSaved: () => void;
  removing: boolean;
}) {
  const [requestKey, setRequestKey] = useState(() => crypto.randomUUID());
  const [amount, setAmount] = useState<number | ''>(currentBonus);
  const [loading, setLoading] = useState(false);
  const notify = useNotify();
  const t = useAdminText();

  useEffect(() => {
    setRequestKey(crypto.randomUUID());
    setAmount(removing ? 0 : currentBonus);
  }, [currentBonus, open, removing]);

  const handleSave = async () => {
    if (amount === '' || !Number.isInteger(amount) || amount < 0 || amount > 10000) {
      notify('Please enter a valid amount (0 or more)', { type: 'warning' });
      return;
    }

    setLoading(true);
    try {
      if (feature?.shared) {
        await adminApi.adjustSharedAllowance(userId, { ...feature.shared, amount: amount - currentBonus, requestId: requestKey });
      } else {
        await adminApi.updateBonusUsage(userId, feature?.feature || '', amount, requestKey, currentBonus);
      }
      notify(
        amount === 0
          ? `Removed bonus for ${feature?.displayName}`
          : `Set bonus for ${feature?.displayName} to ${amount}`,
        { type: 'success' }
      );
      onSaved();
      onClose();
    } catch (error) {
      notify('Bonus could not be updated. Refresh usage if it has changed', { type: 'error' });
      logger.error('Failed to update bonus:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!feature) return null;

  return (
    <Dialog open={open} onClose={() => { if (!loading) onClose(); }} maxWidth="xs" fullWidth>
      <DialogTitle>Edit Bonus: {feature.displayName}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
          Set the bonus allowance for this feature. Use 0 to remove the bonus entirely.
          Current plan limit: {feature.unlimited ? 'Unlimited' : feature.planLimit}
        </Typography>
        {feature.shared && <Alert severity="info" sx={{ mb: 2 }}>{t('pairBonusScope', 'One weekly allowance for both partners. This bonus stays with this couple, including if they reconnect; it never transfers to a different partner.')}</Alert>}
        <TextField
          fullWidth
          label="Bonus Amount"
          type="number"
          value={amount}
          onChange={(e) => {
            const val = e.target.value === '' ? NaN : Number(e.target.value);
            setRequestKey(crypto.randomUUID());
            setAmount(isNaN(val) ? '' : val);
          }}
          slotProps={{ htmlInput: { min: 0, max: 10000 } }}
          disabled={loading}
          helperText={feature.feature === "memory_photos" ? "Personal slots remain until revoked. Reducing the allowance never deletes photos." : "Set to 0 to remove bonus. This is permanent each reset period."}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>Cancel</Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={loading || amount === '' || !Number.isInteger(amount) || amount < 0 || amount > 10000}
        >
          {loading ? 'Saving...' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/**
 * Usage progress bar with color coding
 */
function UsageBar({ used, limit, unlimited }: { used: number; limit: number; unlimited: boolean }) {
  if (unlimited) {
    return (
      <Chip label="Unlimited" size="small" color="success" variant="outlined" />
    );
  }

  const percentage = limit > 0 ? Math.min(100, (used / limit) * 100) : 0;
  const color = percentage >= 90 ? 'error' : percentage >= 70 ? 'warning' : 'primary';

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 160 }}>
      <LinearProgress
        variant="determinate"
        value={percentage}
        color={color}
        sx={{ flex: 1, height: 8, borderRadius: 4 }}
      />
      <Typography variant="caption" sx={{ minWidth: 60, textAlign: 'right' }}>
        {used}/{limit}
      </Typography>
    </Box>
  );
}

export function UserUsageShow() {
  const { userId } = useParams<{ userId: string }>();
  const t = useAdminText();
  const query = useAdminQuery(['admin-user-usage', userId], () => adminApi.getUserUsage(userId!), !!userId);
  const shared = useSharedAllowance(userId ?? '', !!userId);
  const data = query.data;
  const [editFeature, setEditFeature] = useState<FeatureUsage | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [removing, setRemoving] = useState(false);
  const fetchData = () => { void query.refetch(); void shared.refetch(); };
  useEffect(() => { setEditDialogOpen(false); setEditFeature(null); }, [userId]);

  const handleEditBonus = (feature: FeatureUsage) => {
    setRemoving(false);
    setEditFeature(feature);
    setEditDialogOpen(true);
  };

  const handleRemoveBonus = (feature: FeatureUsage) => {
    handleEditBonus(feature);
    setRemoving(true);
  };

  if (query.isPending && !query.isError) {
    return (
      <Box p={3}>
        <LinearProgress />
        <Typography sx={{ mt: 2 }}>Loading usage data...</Typography>
      </Box>
    );
  }

  if (!data) {
    return (
      <Box p={3}>
        <Typography color="error">{query.accessDenied ? t('usageDenied', 'Access to usage was denied.') : t('usageFailed', 'Could not load usage data.')}</Typography>
        {!query.accessDenied && <Button onClick={fetchData}>{t('retry', 'Retry')}</Button>}
        <Button
          component={RouterLink}
          to="/users"
          startIcon={<ArrowBackIcon />}
          sx={{ mt: 2 }}
        >
          Back to Users
        </Button>
      </Box>
    );
  }

  const features: FeatureUsage[] = [...data.features];
  if (shared.data) {
    const { pairId, version, bonus, usage } = shared.data;
    features.push({ feature: 'shared_elisa_replies', displayName: t('sharedElisa', 'Shared Elisa replies'),
      shared: { pairId, version }, bonus, used: usage.used, planLimit: usage.limit === -1 ? -1 : usage.limit - bonus,
      effectiveLimit: usage.limit, remaining: usage.remaining, unlimited: usage.limit === -1, resetPeriod: 'weekly' });
  }

  const effectiveTierColor = data.effectiveTier === 'blaze' ? 'success' : data.effectiveTier === 'flame' ? 'primary' : 'default';
  const ownTierColor = data.planTier === 'blaze' ? 'success' : data.planTier === 'flame' ? 'primary' : 'default';

  return (
    <Box>
      <TopToolbar>
        <Button
          component={RouterLink}
          to={`/users/${userId}/show`}
          startIcon={<ArrowBackIcon />}
          size="small"
        >
          Back to User
        </Button>
      </TopToolbar>

      <Box px={2} pb={3}>
        {/* Breadcrumbs */}
        <Breadcrumbs sx={{ mb: 2 }}>
          <Link component={RouterLink} to="/users" underline="hover" color="inherit">
            Users
          </Link>
          <Link component={RouterLink} to={`/users/${userId}/show`} underline="hover" color="inherit">
            {data.email}
          </Link>
          <Typography color="text.primary">Usage</Typography>
        </Breadcrumbs>

        {/* User Info Header */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Box>
                <Typography variant="h5">
                  Usage Overview
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {data.name || 'No name'} &middot; {data.email}
                </Typography>
              </Box>
              <Box display="flex" gap={1} alignItems="center">
                {data.isSharedSubscription && (
                  <Chip
                    label={`Own: ${PLAN_TIER_FULL_LABELS[data.planTier] || data.planTier}`}
                    color={ownTierColor as any}
                    variant="outlined"
                    size="small"
                  />
                )}
                <Chip
                  label={data.isSharedSubscription
                    ? `Effective: ${PLAN_TIER_FULL_LABELS[data.effectiveTier] || data.effectiveTier}`
                    : PLAN_TIER_FULL_LABELS[data.effectiveTier] || data.effectiveTier}
                  color={effectiveTierColor as any}
                  variant={data.isSharedSubscription ? 'filled' : 'outlined'}
                />
              </Box>
            </Box>

            {/* Partner Sharing Info */}
            {data.partnerInfo && (
              <>
                <Divider sx={{ my: 2 }} />
                <Alert
                  severity={data.isSharedSubscription ? 'info' : 'success'}
                  icon={<PeopleIcon />}
                  sx={{ '& .MuiAlert-message': { width: '100%' } }}
                >
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Box>
                      <Typography variant="body2" fontWeight={500}>
                        {data.isSharedSubscription
                          ? 'Using partner\'s higher subscription'
                          : 'Has a linked partner'}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        Partner:{' '}
                        <Link
                          component={RouterLink}
                          to={`/users/${data.partnerInfo.partnerId}/show`}
                          underline="hover"
                        >
                          {data.partnerInfo.partnerName || data.partnerInfo.partnerEmail}
                        </Link>
                      </Typography>
                    </Box>
                    <Chip
                      label={`Partner: ${PLAN_TIER_FULL_LABELS[data.partnerInfo.partnerTier] || data.partnerInfo.partnerTier}`}
                      size="small"
                      variant="outlined"
                    />
                  </Box>
                </Alert>
              </>
            )}
          </CardContent>
        </Card>

        {query.isError && <Alert severity="warning" action={<Button onClick={fetchData}>{t('retry', 'Retry')}</Button>}>{t('usageStale', 'Could not refresh usage. Showing the last successful result.')}</Alert>}
        {shared.isError && <Alert severity="error" action={!shared.accessDenied && <Button onClick={() => void shared.refetch()}>{t('retry', 'Retry')}</Button>}>{t('sharedAllowanceFailed', 'Could not load the shared allowance.')}</Alert>}
        {shared.isPending && !shared.isError && <LinearProgress aria-label={t('loadingShared', 'Loading shared allowance')} />}
        {/* Usage Table */}
        <TableContainer component={Paper}>
          <AdminTable>
            <TableHead>
              <TableRow>
                <TableCell><strong>Feature</strong></TableCell>
                <TableCell><strong>Usage</strong></TableCell>
                <TableCell align="right"><strong>Plan Limit</strong></TableCell>
                <TableCell align="right"><strong>Bonus</strong></TableCell>
                <TableCell align="right"><strong>Effective Limit</strong></TableCell>
                <TableCell align="right"><strong>Remaining</strong></TableCell>
                <TableCell><strong>Resets</strong></TableCell>
                <TableCell align="center"><strong>Actions</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {features.map((feature) => (
                <TableRow key={feature.feature} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight={500}>
                      {feature.displayName}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {feature.feature}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <UsageBar
                      used={feature.used}
                      limit={feature.effectiveLimit}
                      unlimited={feature.unlimited}
                    />
                  </TableCell>
                  <TableCell align="right">
                    {feature.planLimit === -1 ? (
                      <Chip label="Unlimited" size="small" color="success" variant="outlined" />
                    ) : (
                      feature.planLimit
                    )}
                  </TableCell>
                  <TableCell align="right">
                    {feature.bonus > 0 ? (
                      <Chip
                        label={`+${feature.bonus}`}
                        size="small"
                        color="info"
                        variant="filled"
                      />
                    ) : (
                      <Typography variant="body2" color="textSecondary">-</Typography>
                    )}
                    {!!feature.purchasedSlots && <Typography variant="caption" display="block" color="textSecondary">
                      Purchased photo spaces: {feature.purchasedSlots}
                    </Typography>}
                  </TableCell>
                  <TableCell align="right">
                    {feature.unlimited ? (
                      <Chip label="Unlimited" size="small" color="success" variant="outlined" />
                    ) : (
                      <strong>{feature.effectiveLimit}</strong>
                    )}
                  </TableCell>
                  <TableCell align="right">
                    {feature.unlimited ? (
                      <Typography variant="body2" color="textSecondary">-</Typography>
                    ) : (
                      <Typography
                        variant="body2"
                        color={feature.remaining === 0 ? 'error' : 'inherit'}
                        fontWeight={feature.remaining === 0 ? 600 : 400}
                      >
                        {feature.remaining}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={feature.resetPeriod === 'never' ? 'No reset (retained photos)' : feature.resetPeriod}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Box display="flex" justifyContent="center" gap={0.5}>
                      {feature.bonus > 0 ? (
                        <>
                          <Tooltip title="Edit bonus">
                            <IconButton
                              aria-label="Edit bonus"
                              size="small"
                              disabled={query.isError || (!!feature.shared && shared.isError)}
                              onClick={() => handleEditBonus(feature)}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Remove bonus">
                            <IconButton
                              aria-label="Remove bonus"
                              size="small"
                              color="error"
                              disabled={query.isError || (!!feature.shared && shared.isError)}
                              onClick={() => handleRemoveBonus(feature)}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </>
                      ) : (
                        <Tooltip title="Add bonus">
                          <IconButton
                            aria-label="Add bonus"
                            size="small"
                            color="primary"
                            disabled={query.isError || (!!feature.shared && shared.isError)}
                            onClick={() => handleEditBonus(feature)}
                          >
                            <AddCircleIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </AdminTable>
        </TableContainer>
      </Box>

      <EditBonusDialog
        removing={removing}
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        userId={userId || ''}
        feature={editFeature}
        currentBonus={editFeature?.bonus || 0}
        onSaved={fetchData}
      />
    </Box>
  );
}
